import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'

import {toFloatWithTwoDecimals} from '@vio/core/src/utils/number'

import {
  DetailedPrice,
  PriceTrendsHistoryResponse,
  PriceTrendsParams,
  useGetPriceHistoryQuery
} from '../services/priceInsightsApi'
import {isChartDataValid, isEligibleForPriceInsights} from '../utils/validation'
import {useTaxAdjustment} from './useTaxAdjustment'

interface UsePriceHistoryParams extends Partial<PriceTrendsParams> {
  enabled?: boolean
}

interface UsePriceHistoryReturn {
  priceHistory: PriceTrendsHistoryResponse | undefined
  /**
   * Latest similar-hotels percentile distribution, in the same unit as the
   * adjacent OfferBox card:
   *   - `showTotalPrices=true`  → full-tax stay total (matches OfferBox
   *     "total price with taxes & fees").
   *   - `showTotalPrices=false` → per-night honouring `includeTaxes` /
   *     `includeLocalTaxes` (matches OfferBox "Nightly price").
   *
   * Feeds the visual `PriceRangeBar`. Bubble + boundary labels stay aligned
   * with `primaryOfferData.totalPrice` / `nightlyPrice` (same per-mode
   * contract — see `usePriceTrend`).
   */
  latestDetailedPriceForDisplay: DetailedPrice | null
  /**
   * Latest similar-hotels percentile distribution in the BE's native unit
   * (full-tax **stay total**, irrespective of `showTotalPrices`). Feeds the
   * banner copy classifier inside `buildPriceTrendContent`, which compares
   * against `primaryOfferData.totalPrice` — also a full-tax stay total. The
   * percentage is unit-invariant when both sides share a unit, so the
   * direction is correct regardless of total-vs-nightly view.
   */
  latestDetailedPriceForClassifier: DetailedPrice | null
  isLoading: boolean
  isError: boolean
  error: unknown
}

/**
 * Hook to fetch price history data from the API.
 * Validates that price history has at least 15 days of data before returning it.
 *
 * **Per-mode display contract** mirrors the adjacent OfferBox card so the
 * chart, percentile bar, bubble, and offer card never disagree:
 *   - Total mode: raw API values (always full-tax stay total).
 *   - Nightly mode: divided by `numberOfNights` then tax-pref-adjusted via
 *     `useTaxAdjustment` (subtracts taxes/hotel fees when the user prefs say
 *     so). Matches OfferBox's pref-respecting "Nightly price".
 *
 * The classifier path (`latestDetailedPriceForClassifier`) bypasses both
 * conversions — it stays in BE's native unit so the comparator units match.
 *
 * @param params - Parameters for fetching price history (hotelId, checkIn, checkOut, rooms, currency)
 * @returns Price history data with loading/error states
 */
export const usePriceHistory = (
  params?: UsePriceHistoryParams
): UsePriceHistoryReturn => {
  const {hotelId, checkIn, checkOut, rooms, currency, enabled} = params || {}

  // API always return 1 room, only variation is the number of adults
  const numberOfRooms = 1 // default to 1

  const hasAllParams =
    hotelId && checkIn && checkOut && rooms !== undefined && currency

  // Only fetch if eligible: single room with no children
  const isEligible = isEligibleForPriceInsights(rooms)

  const queryParams = {hotelId, checkIn, checkOut, rooms, currency}

  const {data, isLoading, isError, error} = useGetPriceHistoryQuery(
    queryParams as PriceTrendsParams,
    {
      skip: !hasAllParams || enabled === false || !isEligible
    }
  )

  // `useTaxAdjustment` powers the **nightly branch only**. Total mode keeps
  // raw API values so the chart matches OfferBox's full-tax "total price
  // with taxes & fees" display.
  const {adjustNightlyPrice, numberOfNights, includeTaxes, includeLocalTaxes} =
    useTaxAdjustment({
      hotelId: hotelId ? String(hotelId) : undefined,
      checkIn,
      checkOut
    })

  const showTotalPrices = useSelector(getShowTotalPrices)

  // Per-mode unit conversion:
  //   - Total mode  → raw API value (stay total, full-tax). Matches the
  //     OfferBox card's "total price with taxes & fees" label.
  //   - Nightly mode → divide by `numberOfNights`, then subtract per-night
  //     tax via `adjustNightlyPrice`. Matches the OfferBox card's
  //     pref-respecting "Nightly price" label.
  const adjustHistoryForDisplayUnit = useMemo(() => {
    if (!data) return undefined

    const toDisplay = (value: number) =>
      toFloatWithTwoDecimals(
        showTotalPrices ? value : adjustNightlyPrice(value / numberOfNights)
      )

    const adjustDetailedPrice = (
      detailed: DetailedPrice | undefined
    ): DetailedPrice | undefined => {
      if (!detailed) return undefined
      return {
        mean: toDisplay(detailed.mean),
        median: toDisplay(detailed.median),
        p10: toDisplay(detailed.p10),
        p35: toDisplay(detailed.p35),
        p65: toDisplay(detailed.p65),
        p90: toDisplay(detailed.p90)
      }
    }

    const adjustHistory = (
      history: PriceTrendsHistoryResponse['hotelHistory']
    ) =>
      history.map(pointInTime => {
        const total = pointInTime.price ?? 0
        // `price` is the stay total that the chart's total-mode axis reads.
        // Total mode keeps the raw API stay total; nightly mode never reads
        // `price`, so leaving it as-is here is fine.
        const normalizedTotal = toFloatWithTwoDecimals(total)
        // `nightlyPrice` is the per-night value the chart's nightly-mode
        // axis reads. Always derived as `total / nights` and tax-adjusted
        // so it matches OfferBox's pref-respecting "Nightly price" in
        // nightly mode. In total mode this field is unused.
        const adjustedNightly = toFloatWithTwoDecimals(
          adjustNightlyPrice(total / numberOfNights / numberOfRooms)
        )

        return {
          ...pointInTime,
          price: normalizedTotal,
          nightlyPrice: adjustedNightly,
          detailedPrice: adjustDetailedPrice(pointInTime.detailedPrice)
        }
      })

    return {
      ...data,
      hotelHistory: adjustHistory(data.hotelHistory),
      similarHotelsHistory: adjustHistory(data.similarHotelsHistory ?? [])
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    data,
    adjustNightlyPrice,
    numberOfNights,
    numberOfRooms,
    showTotalPrices,
    includeTaxes,
    includeLocalTaxes
  ])

  // Only return price history if it meets the minimum data requirements (≥15 days + valid data points)
  const validatedPriceHistory = useMemo(() => {
    if (!adjustHistoryForDisplayUnit) return undefined

    // Validate that the data meets requirements for chart rendering
    if (!isChartDataValid(adjustHistoryForDisplayUnit)) {
      return undefined
    }

    return adjustHistoryForDisplayUnit
  }, [adjustHistoryForDisplayUnit])

  // Latest similar-hotels detailed price (display unit) — used by
  // `PriceRangeBar` for boundary labels + bubble alignment.
  // Contract: API returns similar_hotels_history ordered newest-first, so the
  // first entry with non-null detailedPrice is the latest reading. Derived
  // from `validatedPriceHistory` so callers gating on `priceHistory != null`
  // see a consistent state (no stale percentiles when the chart is hidden).
  const latestDetailedPriceForDisplay: DetailedPrice | null = useMemo(() => {
    const history = validatedPriceHistory?.similarHotelsHistory
    if (!history?.length) return null
    const point = history.find(p => p.detailedPrice != null)
    return point?.detailedPrice ?? null
  }, [validatedPriceHistory])

  // Same latest entry, but in BE's native unit (full-tax stay total) — used
  // by the banner classifier so the comparator unit matches
  // `primaryOfferData.totalPrice`. Gated on the same chart-validity check so
  // callers see a coherent loaded/empty state across both flavours.
  const latestDetailedPriceForClassifier: DetailedPrice | null = useMemo(() => {
    if (!validatedPriceHistory) return null
    const history = data?.similarHotelsHistory
    if (!history?.length) return null
    const point = history.find(p => p.detailedPrice != null)
    return point?.detailedPrice ?? null
  }, [data, validatedPriceHistory])

  return {
    priceHistory: validatedPriceHistory,
    latestDetailedPriceForDisplay,
    latestDetailedPriceForClassifier,
    isLoading,
    isError,
    error
  }
}
